import QueryString from 'query-string';
import qs from 'qs';
import { serialize } from 'object-to-formdata';
import Case from 'case';

import { logCloudwatchError } from '@Helpers/util';
import { getAbortControllerSignal } from './aborterHelper';
import { ADMIN_AUTH_API_ERROR } from './actionTypes';
import store from '../store/configureStore';
import getDemoResponse from '../mockData/mockData';

// --- Utility functions ---
export function formatJsonError(errorMessage) {
  if (typeof errorMessage === 'object') {
    const errorKeys = Object.keys(errorMessage);
    const errorMessages = errorKeys.map((key) => {
      const withKey = (key === 'base' || key === 'error') ? '' : `${Case.title(key)} `;
      if (Array.isArray(errorMessage[key])) {
        return `${withKey}${errorMessage[key].join(', ')}`;
      }

      return `${withKey}${errorMessage[key]}`;
    });

    return errorMessages?.join(', ');
  }

  return errorMessage;
}

async function handleErrorResponse(response, { isAdminRoute = false, dispatch } = {}) {
  const json = await response.json();
  const useError = json.error || json.errors || json.message;
  const formattedMessage = formatJsonError(useError);

  // Centralized admin route handling
  if (isAdminRoute && dispatch && (response.status === 401 || response.status === 403)) {
    dispatch({
      type: ADMIN_AUTH_API_ERROR,
      error: { status: response.status, message: formattedMessage },
    });
  }

  logCloudwatchError('ERROR', formattedMessage, response.status);

  throw new Error(formattedMessage);
}

export function extractErrorMessage(jsonErrors) {
  const errorKeys = Object.keys(jsonErrors);
  const errorMessages = errorKeys.map((key) => {
    const withKey = key === 'base' ? '' : `${Case.title(key)} `;
    if (Array.isArray(jsonErrors[key])) {
      return `${withKey}${jsonErrors[key].join(', ')}`;
    }

    return `${withKey}${jsonErrors[key]}`;
  });

  return errorMessages;
}

export function camelizeKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map(camelizeKeys);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      acc[Case.camel(key)] = camelizeKeys(value);
      return acc;
    }, {});
  }
  return obj;
}

function getFullPath(path, version) {
  switch (version) {
    case 'gateway':
      return `${process.env.API_GATEWAY_URL}/api/${path}`;
    case 'local':
      return `${process.env.LOCAL_GATEWAY_URL}/api/${path}`;
    default:
      return `${process.env.API_URL_V2}/${version}/${path}`;
  }
}

// --- Core request functions ---
async function makeRequest({
  method,
  path,
  body = null,
  token = null,
  version = 'v1',
  signal = null,
  customError = false,
  isAdminRoute = false,
  dispatch = null,
}) {
  const options = {
    method,
    signal,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Key-Inflection': 'camel',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  // Demo mode: intercept all product API calls and return mock data
  if (store.getState()?.auth?.isDemo) return getDemoResponse(path);

  // Route to API Gateway, Rails, or local based on version
  const fullPath = getFullPath(path, version);

  const response = await fetch(fullPath, options);

  if (!response.ok && !customError) {
    await handleErrorResponse(response, { isAdminRoute, dispatch });
  }

  return response;
}

async function makeMixedFormRequest({
  path,
  formData,
  token = null,
  version = 'v1',
  method = 'POST',
  customError = false,
  isAdminRoute = false,
  dispatch = null,
}) {
  const options = {
    method,
    body: formData,
    credentials: 'include',
    headers: { 'Key-Inflection': 'camel' },
  };

  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  // Demo mode: block form uploads silently
  if (store.getState()?.auth?.isDemo) return getDemoResponse(path);

  // Route to API Gateway, Rails, or local based on version
  const fullPath = getFullPath(path, version);

  const response = await fetch(fullPath, options);

  if (!response.ok && !customError) {
    await handleErrorResponse(response, { isAdminRoute, dispatch });
  }

  return response;
}

export async function makeExportRequest({
  path,
  token = null,
  version = 'v1',
  filename = 'redflag-export',
  signal = null,
  customError = false,
  isAdminRoute = false,
  dispatch = null,
}) {
  const options = {
    method: 'GET',
    signal,
    credentials: 'include',
    headers: {
      'Content-Type': 'text/csv',
      Accept: 'text/csv',
      'Key-Inflection': 'camel',
    },
  };

  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  // Route to API Gateway or Rails based on version
  const fullPath = getFullPath(path, version);

  const response = await fetch(fullPath, options);

  if (!response.ok && !customError) {
    await handleErrorResponse(response, { isAdminRoute, dispatch });
  }

  // Format date as MM-DD-YYYY
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const year = now.getFullYear();
  const formattedDate = `${month}-${day}-${year}`;
  const finalFilename = `${filename}-${formattedDate}.csv`;

  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = finalFilename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// --- Exported API functions ---
export async function get(options) {
  const getPath = () => {
    if (options.params) {
      return `${options.path}?${QueryString.stringify(options.params, {
        arrayFormat: 'bracket',
      })}`;
    }

    return options.path;
  };

  return makeRequest({
    ...options,
    method: 'GET',
    path: getPath(),
    signal: getAbortControllerSignal(),
  });
}

export async function nestedParamsGet(options) {
  const params = qs.stringify(options.params, { arrayFormat: 'brackets' });

  return makeRequest({
    ...options,
    method: 'GET',
    path: `${options.path}?${params}`,
    signal: getAbortControllerSignal(),
  });
}

export async function getCSV(options) {
  const getPath = () => {
    if (options.params) {
      const params = qs.stringify(options.params, { arrayFormat: 'brackets' });
      return `${options.path}.csv?${params}`;
    }

    return `${options.path}.csv`;
  };

  return makeExportRequest({
    ...options,
    path: getPath(),
    signal: getAbortControllerSignal(),
  });
}

export async function getExport(options) {
  const getPath = () => {
    if (options.params) {
      const params = qs.stringify(options.params, { arrayFormat: 'brackets' });
      return `${options.path}?${params}`;
    }

    return `${options.path}`;
  };

  return makeExportRequest({
    ...options,
    path: getPath(),
    signal: getAbortControllerSignal(),
  });
}

export async function put(options) {
  return makeRequest({ ...options, method: 'PUT' });
}

export async function post(options) {
  return makeRequest({ ...options, method: 'POST' });
}

export async function patch(options) {
  return makeRequest({ ...options, method: 'PATCH' });
}

export async function del(options) {
  return makeRequest({ ...options, method: 'DELETE' });
}

export async function uploadFile(options) {
  const formData = new FormData();
  formData.append('file', options.file);

  return makeMixedFormRequest({ ...options, formData, method: options.method ?? 'POST' });
}

export async function mixedFormDataRequest(options) {
  const formData = serialize(options.body);

  return makeMixedFormRequest({ ...options, formData, method: options.method ?? 'POST' });
}
